import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { RUN_STAGES, RUN_STATUSES } from '@services/fmdc';
import { Dot } from './StatusPill';
import { stageModel, STAGE_STATES } from './stageModel';
import { useNowTick } from './useFmdcPoll';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { ErrorBox } from './styles';
import { formatElapsed, formatSeconds, formatStage } from './format';

// The seven-stage pipeline as a row of dots. Three long waits live on the run page and this is
// where the first one is explained: on dev a run sits in `pending` for 2–3 minutes with nothing
// else visible, so the active stage names itself and says how long it has been running.

const STATE_TOKENS = {
  [STAGE_STATES.DONE]: 'positive',
  [STAGE_STATES.ACTIVE]: 'primaryCtaButton',
  [STAGE_STATES.FAILED]: 'negative',
  [STAGE_STATES.PENDING]: 'secondaryText',
};

const Steps = styled.ol`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  list-style: none;
`;

// Each step draws the connector as a line through its own dot's centre; the first and last steps
// start and stop the line at the dot so the row has no loose ends.
const Step = styled.li`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 4px;

  &::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  &:first-child::before {
    left: 50%;
  }

  &:last-child::before {
    right: 50%;
  }
`;

const StepDot = styled(Dot)`
  position: relative;
  z-index: 1;
`;

const Label = styled.div`
  margin-top: 8px;
`;

const activeStage = (run) => (RUN_STAGES.includes(run?.status) ? run.status : null);

export const StageStepper = ({ run }) => {
  const stages = stageModel(run);
  const stage = activeStage(run);
  const isErrored = run?.status === RUN_STATUSES.ERROR;
  const isStuck = run?.stuck === true;
  const isMoving = !!stage && !isStuck;
  const now = useNowTick(30000, isMoving);

  return (
    <div>
      <Steps role="list" aria-label="Run stages">
        {stages.map((s) => (
          <Step key={s.key} role="listitem" aria-current={s.state === STAGE_STATES.ACTIVE ? 'step' : undefined}>
            <StepDot $token={STATE_TOKENS[s.state]} $pulse={s.state === STAGE_STATES.ACTIVE && !isStuck} />
            <Label>
              <Body7 bold={s.state === STAGE_STATES.ACTIVE}>{s.label}</Body7>
            </Label>
            {s.seconds !== null && (
              <Container top={2}>
                <Body7 isLowContrast>{formatSeconds(s.seconds)}</Body7>
              </Container>
            )}
          </Step>
        ))}
      </Steps>

      {isMoving && (
        <Container top={12}>
          <Body5 isLowContrast role="status">
            {`${formatStage(stage)} · running for ${formatElapsed(run.created_at, now)}`}
            {stage === RUN_STATUSES.PENDING ? ' (ingestion usually takes 2 to 3 minutes)' : ''}
          </Body5>
        </Container>
      )}

      {isStuck && (
        <Container top={12}>
          <NoticeText tone={NOTICE_TONES.WARNING}>No progress for 6 hours. Rerun to try again.</NoticeText>
        </Container>
      )}

      {isErrored && (
        <Container top={16}>
          <ErrorBox role="alert">
            <Body5 bold>The run failed</Body5>
            <Container top={6}>
              <Body5>{run.error || 'No error message was recorded.'}</Body5>
            </Container>
          </ErrorBox>
        </Container>
      )}
    </div>
  );
};
